import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';

/* ---- Login button (unauthenticated) ---- */

const GoogleButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  background: ${props => props.theme.colors.bgTertiary};
  color: ${props => props.theme.colors.textSecondary};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: 8px;
  cursor: pointer;
  font-size: 0.8rem;
  font-weight: 500;
  font-family: ${props => props.theme.fonts.main};
  transition: all 0.2s;
  white-space: nowrap;

  &:hover {
    color: ${props => props.theme.colors.text};
    background: ${props => props.theme.colors.bgHover};
  }

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    padding: 5px 10px;
    font-size: 0.75rem;
  }
`;

/* ---- Account button + dropdown (authenticated) ---- */

const AccountWrapper = styled.div`
  position: relative;
`;

const AccountButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 8px 4px 4px;
  background: transparent;
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: 20px;
  cursor: pointer;
  transition: all 0.2s;
  color: ${props => props.theme.colors.text};

  &:hover {
    background: ${props => props.theme.colors.bgTertiary};
    border-color: ${props => props.theme.colors.textSecondary};
  }
`;

const Avatar = styled.img`
  width: 26px;
  height: 26px;
  border-radius: 50%;
  flex-shrink: 0;
`;

const AvatarFallback = styled.div`
  width: 26px;
  height: 26px;
  border-radius: 50%;
  background: ${props => props.theme.colors.primary};
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.7rem;
  font-weight: 700;
  flex-shrink: 0;
`;

const ChevronDown = styled.svg`
  width: 12px;
  height: 12px;
  color: ${props => props.theme.colors.textSecondary};
  transition: transform 0.2s;
  ${props => props.$open && 'transform: rotate(180deg);'}
`;

const Dropdown = styled.div`
  position: absolute;
  top: calc(100% + 6px);
  right: 0;
  min-width: 200px;
  background: ${props => props.theme.colors.bgSecondary};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: 10px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.4);
  z-index: 200;
  padding: 6px 0;
  opacity: ${props => props.$open ? 1 : 0};
  visibility: ${props => props.$open ? 'visible' : 'hidden'};
  transform: ${props => props.$open ? 'translateY(0)' : 'translateY(-4px)'};
  transition: opacity 0.15s, transform 0.15s, visibility 0.15s;
`;

const DropdownHeader = styled.div`
  padding: 10px 14px;
  border-bottom: 1px solid ${props => props.theme.colors.border};
  margin-bottom: 4px;
`;

const DropdownName = styled.div`
  font-size: 0.85rem;
  font-weight: 600;
  color: ${props => props.theme.colors.text};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const DropdownEmail = styled.div`
  font-size: 0.72rem;
  color: ${props => props.theme.colors.textSecondary};
  margin-top: 2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const DropdownItem = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 9px 14px;
  background: transparent;
  border: none;
  color: ${props => props.$danger ? (props.theme.colors.error || '#EF4444') : props.theme.colors.text};
  font-size: 0.82rem;
  font-weight: 500;
  font-family: ${props => props.theme.fonts.main};
  cursor: pointer;
  text-align: left;
  transition: background 0.15s;

  &:hover {
    background: ${props => props.theme.colors.bgTertiary};
  }
`;

/* ---- Icons ---- */

const UserIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
    <circle cx="12" cy="7" r="4" />
  </svg>
);

const LogoutIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
    <polyline points="16 17 21 12 16 7" />
    <line x1="21" y1="12" x2="9" y2="12" />
  </svg>
);

const DropdownDivider = styled.div`
  height: 1px;
  background: ${props => props.theme.colors.border};
  margin: 4px 0;
`;

/* ---- Component ---- */

const LoginButton = () => {
  const { isAuthenticated, user, logout } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);

  // Close on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    if (open) {
      document.addEventListener('mousedown', handleClick);
    }
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  // Close on escape
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    if (open) {
      document.addEventListener('keydown', handleEscape);
    }
    return () => document.removeEventListener('keydown', handleEscape);
  }, [open]);

  if (isAuthenticated) {
    const initial = (user.name || 'U').charAt(0).toUpperCase();

    return (
      <AccountWrapper ref={wrapperRef}>
        <AccountButton onClick={() => setOpen(prev => !prev)}>
          {user.avatar_url
            ? <Avatar src={user.avatar_url} alt="" />
            : <AvatarFallback>{initial}</AvatarFallback>
          }
          <ChevronDown $open={open} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="6 9 12 15 18 9" />
          </ChevronDown>
        </AccountButton>

        <Dropdown $open={open}>
          <DropdownHeader>
            <DropdownName>{user.name || 'User'}</DropdownName>
            {user.email && <DropdownEmail>{user.email}</DropdownEmail>}
          </DropdownHeader>
          <DropdownItem onClick={() => { setOpen(false); navigate('/my-diagnoses'); }}>
            <UserIcon />
            {t('マイ診断', 'My Results')}
          </DropdownItem>
          {/* B2B「組織に参加」は公開導線から除外 (2026-05-15 / 2026-05-19 補修) */}
          <DropdownDivider />
          <DropdownItem $danger onClick={() => { setOpen(false); logout(); }}>
            <LogoutIcon />
            {t('ログアウト', 'Logout')}
          </DropdownItem>
        </Dropdown>
      </AccountWrapper>
    );
  }

  return (
    <GoogleButton onClick={() => navigate('/login')}>
      {t('ログイン', 'Login')}
    </GoogleButton>
  );
};

export default LoginButton;
